/* eslint-env jest */
import {
  LAYOUTS, BG_SUBSTRINGS, selectLayout, withFreeMagnets, FREE_MAGNETS_SLOT, BUY_ALL_TILE_LAYOUT,
  namedLayoutFor,
} from './packageLayouts';
import fixture from './packageLayouts.fixture.json';

// packageLayouts.fixture.json is a snapshot of every real package option in the
// database ([packageId, optionName, expectedLayoutKey|null]) captured from the
// ORIGINAL AbsolutePosImgs component. It is the contract that the data-driven
// replacement reproduces the legacy preview choice for every option. If you
// intentionally change a layout's selection, regenerate the fixture in the same
// commit.

describe('selectLayout — parity with the legacy component', () => {
  it('reproduces the legacy layout choice for every real option (all 2,322)', () => {
    const mismatches = fixture
      .filter(([packageId, optionName, expected]) => selectLayout(packageId, optionName) !== expected)
      .map(([packageId, optionName, expected]) => ({
        packageId, optionName, expected, got: selectLayout(packageId, optionName),
      }));
    expect(mismatches).toEqual([]);
  });

  it('never returns a key that is missing from LAYOUTS', () => {
    const badKeys = fixture
      .map(([packageId, optionName]) => selectLayout(packageId, optionName))
      .filter((key) => key !== null && !LAYOUTS[key]);
    expect(badKeys).toEqual([]);
  });

  it('treats optionName matching as substring (loose), like the legacy code', () => {
    expect(selectLayout(629, 'Star Student Package 2027')).toBe('p629StarStudent');
    expect(selectLayout(611, 'Picture Perfect Package (Fall)')).toBe('picturePerfect');
  });

  it('accepts a string packageId (legacy used loose ==)', () => {
    expect(selectLayout('629', 'Star Student Package')).toBe('p629StarStudent');
    expect(selectLayout('610', 'Double Double')).toBe('p610DoubleDouble');
    expect(selectLayout('549', 'All Star')).toBe('starStudent');
  });

  it('scopes Star/Memories to their packages and routes 629 to its own layouts', () => {
    expect(selectLayout(549, 'All Star')).toBe('starStudent');
    expect(selectLayout(611, 'Star Student Package')).toBe('starStudent');
    expect(selectLayout(629, 'Star Student Package')).toBe('p629StarStudent'); // 629 override
    expect(selectLayout(999, 'All Star')).toBeNull(); // not a Star package
    expect(selectLayout(629, 'Memories Package')).toBe('p629Memories'); // 629 override
    expect(selectLayout(611, 'Memories Package')).toBe('memories');
  });

  it('routes the 2026-27 fall "Star Student" top tier to its own layout, scoped to 877/778', () => {
    expect(selectLayout(877, 'Star Student')).toBe('starStudent2027'); // EL Fall 2027
    expect(selectLayout(778, 'Star Student')).toBe('starStudent2027'); // JH-HS Fall 2027
    expect(selectLayout('877', 'Star Student Collection')).toBe('starStudent2027');
    expect(selectLayout(584, 'Star Student Package')).toBeNull(); // retired package keeps no preview
    expect(selectLayout(611, 'Star Student Package')).toBe('starStudent'); // older layout untouched
    expect(LAYOUTS.starStudent2027.filter(Boolean)).toHaveLength(13); // 8x10, 2x 5x7, 3x5, 8 wallets, phone
  });

  it('returns null for options with no preview layout', () => {
    expect(selectLayout(611, 'Magnets')).toBeNull();
    expect(selectLayout(611, 'Buy All Your Photos')).toBeNull();
    expect(selectLayout(123, 'Some Unknown Package')).toBeNull();
  });
});

describe('LAYOUTS — structural integrity', () => {
  const POSE_ROLES = new Set([
    'pose1', 'pose2', 'pose3', 'pose4', 'poseHoriz', 'springpose1', 'springpose2',
  ]);

  it('has exactly the 27 known layouts', () => {
    expect(Object.keys(LAYOUTS)).toHaveLength(27);
  });

  it('every layout is an array of 16 slots', () => {
    Object.values(LAYOUTS).forEach((slots) => {
      expect(Array.isArray(slots)).toBe(true);
      expect(slots).toHaveLength(16);
    });
  });

  it('every visible slot has valid geometry, a known pose role, and a known/blank background', () => {
    Object.entries(LAYOUTS).forEach(([key, slots]) => {
      slots.forEach((slot, i) => {
        if (slot === null) return; // hidden slot
        const where = `${key}[${i}]`;
        expect(slot.top).toMatch(/%$/);
        expect(slot.left).toMatch(/%$/);
        expect(slot.width).toMatch(/%$/);
        expect(POSE_ROLES.has(slot.pose)).toBe(true);
        if (slot.background !== null) {
          expect(BG_SUBSTRINGS[slot.background]).toBeDefined();
        }
        expect(where).toBeTruthy();
      });
    });
  });

  it('every layout has at least one visible slot', () => {
    Object.values(LAYOUTS).forEach((slots) => {
      expect(slots.some((s) => s !== null)).toBe(true);
    });
  });
});

describe('withFreeMagnets — the offer-conditional magnet slot (issue #5218)', () => {
  it('drops the magnet slot into the first hidden slot, leaving the layout untouched', () => {
    const base = LAYOUTS.starStudent2027;
    const out = withFreeMagnets(base);
    expect(out).not.toBe(base);
    expect(out).toHaveLength(16);
    expect(out[13]).toBe(FREE_MAGNETS_SLOT);
    expect(base[13]).toBeNull();
    expect(out.slice(0, 13)).toEqual(base.slice(0, 13));
  });

  it('appends when the layout has no free slot', () => {
    const full = LAYOUTS.keepsake.map((slot) => slot || FREE_MAGNETS_SLOT);
    expect(withFreeMagnets(full)).toHaveLength(17);
    expect(withFreeMagnets(full)[16]).toBe(FREE_MAGNETS_SLOT);
  });

  it('passes a missing layout through', () => {
    expect(withFreeMagnets(null)).toBeNull();
    expect(withFreeMagnets(undefined)).toBeUndefined();
  });

  it('is a framed pose1 slot -- the same shape as every other slot', () => {
    expect(FREE_MAGNETS_SLOT).toMatchObject({ pose: 'pose1', frame: 'magnet', background: null });
    expect(FREE_MAGNETS_SLOT.top).toMatch(/%$/);
  });

  it('never touches selectLayout (the parity fixture stays the contract)', () => {
    expect(selectLayout(877, 'Star Student')).toBe('starStudent2027');
    expect(Object.values(LAYOUTS).every((slots) => slots.every((s) => !s || !s.frame))).toBe(true);
  });

  it('the Buy All tile is a phone + a magnet', () => {
    expect(BUY_ALL_TILE_LAYOUT.map((s) => s.frame)).toEqual(['phone', 'magnet']);
  });
});

describe('namedLayoutFor - the Buy All tile magnet slot is offer-gated', () => {
  const named = { buyAllTile: BUY_ALL_TILE_LAYOUT };
  it('keeps the FREE-tagged magnet only while the free-magnets tier is live', () => {
    expect(namedLayoutFor('buyAllTile', true, named).some((s) => s?.frame === 'magnet')).toBe(true);
    expect(namedLayoutFor('buyAllTile', false, named).some((s) => s?.frame === 'magnet')).toBe(false);
  });
  it('keeps the phone slot either way and returns null for unknown keys', () => {
    expect(namedLayoutFor('buyAllTile', false, named).some((s) => s?.frame === 'phone')).toBe(true);
    expect(namedLayoutFor('nope', true, named)).toBeNull();
  });
});
